import { useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Menu, User, BookOpen, Video, Headphones, Library } from 'lucide-react';
import NotificationBell from './NotificationBell';
import { LanguageSelector } from '../context/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

function Navbar() {
  const { user, logout } = useContext(AuthContext);

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex justify-between items-center h-16">
          {/* Mobile menu + Logo */}
          <div className="flex items-center gap-2">
            {/* Mobile hamburger */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild className="md:hidden">
                <Button variant="ghost" size="sm" className="p-1" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                <DropdownMenuItem asChild>
                  <Link to="/" className="cursor-pointer" data-testid="mobile-nav-home">Home</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/all-courses" className="cursor-pointer" data-testid="mobile-nav-courses">Courses</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/ebooks" className="cursor-pointer flex items-center gap-2" data-testid="mobile-nav-ebooks">
                    <BookOpen className="w-4 h-4" /> eBooks
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/live-classes" className="cursor-pointer flex items-center gap-2" data-testid="mobile-nav-live-classes">
                    <Video className="w-4 h-4" /> Live Classes
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/teachers" className="cursor-pointer">Teachers</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/audio-rooms" className="cursor-pointer flex items-center gap-2">
                    <Headphones className="w-4 h-4" /> Audio Rooms
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/" className="text-2xl font-black text-indigo-600" data-testid="logo">
              Darasani
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-slate-700 hover:text-indigo-600 font-medium" data-testid="nav-home">Home</Link>
            <Link to="/all-courses" className="text-slate-700 hover:text-indigo-600 font-medium" data-testid="nav-courses">Courses</Link>
            <Link to="/ebooks" className="text-slate-700 hover:text-indigo-600 font-medium" data-testid="nav-ebooks">eBooks</Link>
            <Link to="/live-classes" className="text-slate-700 hover:text-indigo-600 font-medium" data-testid="nav-live-classes">Live Classes</Link>
            <Link to="/teachers" className="text-slate-700 hover:text-indigo-600 font-medium" data-testid="nav-teachers">Teachers</Link>
            <Link to="/audio-rooms" className="text-slate-700 hover:text-indigo-600 font-medium" data-testid="nav-audio-rooms">Audio Rooms</Link>
          </div>

          <div className="flex items-center gap-2">
            <LanguageSelector className="!bg-white !text-slate-700 !border-slate-200 text-xs py-1 px-2" />
            {user && (
              <NotificationBell />
            )}
            {user && user.role === 'student' && (
              <Link to="/library" className="relative p-2 text-slate-600 hover:text-indigo-600 transition-colors" data-testid="nav-library-btn" title="My Library">
                <Library className="w-5 h-5" />
              </Link>
            )}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                    <User className="w-5 h-5" />
                    <span className="hidden md:inline">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to={user.role === 'teacher' ? '/teacher/dashboard' : user.role === 'admin' ? '/admin' : '/profile'} className="cursor-pointer" data-testid="nav-dashboard">
                      {user.role === 'student' ? 'My Profile' : 'Dashboard'}
                    </Link>
                  </DropdownMenuItem>
                  {user.role === 'student' && (
                    <DropdownMenuItem asChild>
                      <Link to="/library" className="cursor-pointer" data-testid="nav-library">My Library</Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === 'student' && (
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="cursor-pointer" data-testid="nav-profile">Profile Settings</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-600" data-testid="logout-btn">
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button asChild data-testid="nav-login-btn">
                <Link to="/login">Login</Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;